import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { Spin, Result, Button } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { SSO_CALLBACK_CODE_KEY_PREFIX } from "constants/auth";
import {
  getAccessToken,
  getAssociatedSubscriptionsFromResponse,
  getCurrentSubscriptionId,
  getIsSSOLogin,
  getSubscriptions,
  setAccessToken,
  setAssociatedSubscriptions,
  setRefreshToken,
  setIDToken,
  setCurrentSubscriptionId,
  setIsSSOLogin,
  markAuthSessionActive,
  resolveAuthenticatedAuthRouteRedirect,
  ssoCallback
} from "services/auth";
import ErrorManager from "utils/cumulative-error-message/error-manager";
import { SentryErrorSources } from "constants/index";
import {
  consumeSsoPostLoginRedirect,
  getGenericPostLoginRedirectUrl,
  getProjectInviteRedirectUrlFromParams,
  resolveSubscriptionForProjectInvite
} from "utils/project-invite-redirect";

/** One in-flight exchange per OAuth code (survives Strict Mode remount). */
const ssoExchangeByCode = new Map<string, Promise<void>>();

function getSsoCallbackGuardKey(code: string): string {
  return `${SSO_CALLBACK_CODE_KEY_PREFIX}${code}`;
}

function getSsoCallbackDestinationKey(guardKey: string): string {
  return `${guardKey}_destination`;
}

function resolveSsoCallbackDestination(
  subscriptions: Array<{ id: string }>,
  postLoginRedirectUrl: string | null
): string {
  if (subscriptions.length === 1) {
    return postLoginRedirectUrl || "/projects";
  }

  const inviteSubscriptionId = resolveSubscriptionForProjectInvite(
    postLoginRedirectUrl,
    subscriptions
  );
  if (inviteSubscriptionId && postLoginRedirectUrl) {
    return postLoginRedirectUrl;
  }
  if (postLoginRedirectUrl) {
    return `/subscription-select?redirectUrl=${encodeURIComponent(postLoginRedirectUrl)}`;
  }
  return "/subscription-select";
}

function applySsoCallbackNavigation(
  history: ReturnType<typeof useHistory>,
  subscriptions: Array<{ id: string }>,
  postLoginRedirectUrl: string | null
): string {
  const destination = resolveSsoCallbackDestination(
    subscriptions,
    postLoginRedirectUrl
  );

  if (subscriptions.length === 1) {
    setCurrentSubscriptionId(subscriptions[0].id);
  } else {
    const inviteSubscriptionId = resolveSubscriptionForProjectInvite(
      postLoginRedirectUrl,
      subscriptions
    );
    if (inviteSubscriptionId && postLoginRedirectUrl) {
      setCurrentSubscriptionId(inviteSubscriptionId);
    }
  }

  history.replace(destination);
  return destination;
}

function handleMissingAuthCode(
  history: ReturnType<typeof useHistory>,
  search: string,
  hash: string,
  setError: (error: string | null) => void
): void {
  const redirectPath = resolveAuthenticatedAuthRouteRedirect(search, hash);
  if (redirectPath) {
    history.replace(redirectPath);
    return;
  }
  if (getAccessToken()) {
    history.replace(
      getCurrentSubscriptionId() ? "/projects" : "/subscription-select"
    );
    return;
  }
  if (getIsSSOLogin()) {
    history.replace("/subscription-select");
    return;
  }
  const errStr = "No authorization code received. Please try logging in again.";
  setError(errStr);
  ErrorManager().handleError({
    error: errStr,
    showError: false,
    errorSource: SentryErrorSources.api
  });
}

function handleAlreadyCompletedSsoCallback(
  guardKey: string,
  history: ReturnType<typeof useHistory>
): boolean {
  if (sessionStorage.getItem(guardKey) !== "done") return false;

  const storedDestination = sessionStorage.getItem(
    getSsoCallbackDestinationKey(guardKey)
  );
  if (getAccessToken() && storedDestination) {
    history.replace(storedDestination);
  }
  return true;
}

function clearSsoCallbackSession(guardKey: string): void {
  if (!guardKey) return;
  sessionStorage.removeItem(guardKey);
  sessionStorage.removeItem(getSsoCallbackDestinationKey(guardKey));
}

/** Clears in-flight exchange cache (tests only). */
export function clearSsoExchangeCacheForTests(): void {
  ssoExchangeByCode.clear();
}

function SSOCallback() {
  const history = useHistory();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let guardKey = "";

    const run = async () => {
      try {
        const searchParams = new URLSearchParams(location.search);
        const code = searchParams.get("code");

        if (!code) {
          handleMissingAuthCode(
            history,
            location.search,
            location.hash,
            setError
          );
          return;
        }

        guardKey = getSsoCallbackGuardKey(code);
        if (handleAlreadyCompletedSsoCallback(guardKey, history)) {
          return;
        }

        let exchange = ssoExchangeByCode.get(code);
        if (!exchange) {
          exchange = exchangeSsoCode(code, guardKey, history, location);
          ssoExchangeByCode.set(code, exchange);
        }
        await exchange;
      } catch (err: any) {
        clearSsoCallbackSession(guardKey);
        setError(err?.message || "An error occurred during authentication.");
        ErrorManager().handleError({
          error: err,
          showError: false,
          errorSource: SentryErrorSources.api
        });
      }
    };

    run();
    // OAuth callback is one-shot: read location at mount only. Module + sessionStorage
    // dedup handles Strict Mode remounts; rerenders with a new code are ignored.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only by design
  }, []);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Result
          status="error"
          title="Authentication Failed"
          subTitle={error}
          extra={
            <Button type="primary" onClick={() => history.push("/")}>
              Back to Login
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen flex-col gap-4">
      <Spin
        indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
        size="large"
      />
      <p className="text-center text-gray-600">Completing your sign-in...</p>
    </div>
  );
}

async function exchangeSsoCode(
  code: string,
  guardKey: string,
  history: ReturnType<typeof useHistory>,
  location: ReturnType<typeof useLocation>
): Promise<void> {
  try {
    const res: any = await ssoCallback({
      code
    });

    if (res?.status === 200 && res?.data.id_token) {
      setIDToken(res?.data.id_token);
      setAccessToken(res?.data.access_token);
      setRefreshToken(res?.data.refresh_token);
      setIsSSOLogin("true");
      markAuthSessionActive();

      const subscriptionsRes: any = await getSubscriptions(
        res?.data.access_token
      );
      if (subscriptionsRes?.status !== 200) {
        throw new Error(
          subscriptionsRes?.response?.data?.error ||
            subscriptionsRes?.message ||
            "Unable to fetch subscriptions"
        );
      }

      const subscriptions =
        getAssociatedSubscriptionsFromResponse(subscriptionsRes);
      setAssociatedSubscriptions(subscriptions);

      const postLoginRedirectUrl =
        consumeSsoPostLoginRedirect() ||
        getProjectInviteRedirectUrlFromParams(location.search, location.hash) ||
        getGenericPostLoginRedirectUrl(location.search);

      const destination = applySsoCallbackNavigation(
        history,
        subscriptions,
        postLoginRedirectUrl
      );
      sessionStorage.setItem(guardKey, "done");
      sessionStorage.setItem(
        getSsoCallbackDestinationKey(guardKey),
        destination
      );
      ssoExchangeByCode.delete(code);
    } else {
      sessionStorage.removeItem(guardKey);
      sessionStorage.removeItem(getSsoCallbackDestinationKey(guardKey));
      throw new Error("Authentication failed. Please try logging in again.");
    }
  } catch (exchangeError: any) {
    sessionStorage.removeItem(guardKey);
    sessionStorage.removeItem(getSsoCallbackDestinationKey(guardKey));
    ssoExchangeByCode.delete(code);
    console.error("Token exchange error:", exchangeError);
    throw new Error(
      exchangeError?.message || "An error occurred during token exchange."
    );
  }
}

export default SSOCallback;
